// Client for the RhinoAnalyst ("Ask Rhino") service.
//
// The service verifies the same Auth0 bearer token rhinoapi receives and asks
// rhinoapi's checkToken which organization is active, so the browser session
// cookie must travel with the request (credentials: 'include'). The response is
// a server-sent event stream; this module turns it into callbacks.

import axios from 'axios';
import AuthService from './AuthService';
import StorageService from './StorageService';

const BASE_URL = (process.env.REACT_APP_ANALYST_BASE_URL || '').replace(/\/$/, '');

export const isRhinoAnalystConfigured = () => !!BASE_URL;

async function getAuthorizationHeader() {
  if (StorageService.readEntry('authType') === 'auth0') {
    const token = await AuthService.getAccessToken();
    if (token) return `Bearer ${token}`;
  }
  // Legacy cookie sessions: mirrors AxiosHelper, which sends the placeholder
  // header so the API gateway forwards the request.
  return axios?.defaults?.headers?.common?.Authorization || 'legacy';
}

function parseSseChunk(chunk) {
  // One SSE message: lines of "event: x" / "data: y". Comment lines start with ":".
  let event = 'message';
  const dataLines = [];
  chunk.split('\n').forEach((line) => {
    if (line.startsWith(':')) return;
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  });
  if (!dataLines.length) return null;
  try {
    return { event, data: JSON.parse(dataLines.join('\n')) };
  } catch (err) {
    return null;
  }
}

/**
 * Stream one chat turn.
 *
 * @param {Object} options
 * @param {Array<{role: 'user'|'assistant', content: string}>} options.messages full text history
 * @param {{from: string, to: string}} [options.dateRange] YYYY-MM-DD range selected in the UI
 * @param {{page: string, filters: Object<string,string>}} [options.context] what the user is viewing
 * @param {string} [options.conversationId]
 * @param {AbortSignal} [options.signal]
 * @param {(event: string, data: Object) => void} options.onEvent called for every SSE event
 */
export async function streamChat({
  messages, dateRange, context, conversationId, signal, onEvent,
}) {
  if (!BASE_URL) {
    throw new Error('REACT_APP_ANALYST_BASE_URL is not configured');
  }
  const body = { messages };
  if (conversationId) body.conversation_id = conversationId;
  if (dateRange) body.date_range = dateRange;
  if (context) body.context = context;

  const response = await fetch(`${BASE_URL}/v1/chat`, {
    method: 'POST',
    credentials: 'include',
    signal,
    headers: {
      Authorization: await getAuthorizationHeader(),
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let detail = '';
    try {
      detail = (await response.json())?.detail || '';
    } catch (err) { /* non-JSON error body */ }
    const error = new Error(typeof detail === 'string' && detail ? detail : `Request failed (${response.status})`);
    error.status = response.status;
    if (detail && typeof detail === 'object') error.detail = detail; // e.g. { code: 'limit_reached', turns, limit }
    throw error;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read(); // eslint-disable-line no-await-in-loop
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary = buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const parsed = parseSseChunk(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);
      if (parsed) onEvent(parsed.event, parsed.data);
      boundary = buffer.indexOf('\n\n');
    }
  }
  const tail = parseSseChunk(buffer);
  if (tail) onEvent(tail.event, tail.data);
}

async function apiRequest(path, { method = 'GET', signal } = {}) {
  if (!BASE_URL) {
    throw new Error('REACT_APP_ANALYST_BASE_URL is not configured');
  }
  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    credentials: 'include',
    signal,
    headers: { Authorization: await getAuthorizationHeader(), Accept: 'application/json' },
  });
  if (!response.ok) {
    const error = new Error(`Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  if (response.status === 204) return null;
  return response.json();
}

// Chat history lives in the service (per user + organization); the browser keeps
// nothing. Each call is scoped by the caller's session.
export const listConversations = (options) => apiRequest('/v1/conversations', options);
export const getConversation = (id, options) => apiRequest(`/v1/conversations/${encodeURIComponent(id)}`, options);
export const deleteConversationRemote = (id) => apiRequest(`/v1/conversations/${encodeURIComponent(id)}`, { method: 'DELETE' });

// Month-to-date usage for the caller's organization (and the CCR view of any org).
export const getUsage = (options) => apiRequest('/v1/usage', options);
export const getOrganizationUsage = (organizationId, options) => apiRequest(`/v1/admin/organizations/${organizationId}/usage`, options);

// Friendly labels for the tools the service reports in tool_call events.
export const TOOL_LABELS = {
  get_call_volume: 'Call volume',
  get_missed_call_rate: 'Missed calls',
  get_call_answer_times: 'Answer times',
  get_voicemail_stats: 'Voicemails',
  get_message_volume: 'Message volume',
  get_message_peak_hours: 'Message peak hours',
  get_message_response_times: 'Message response times',
  get_messages_by_member: 'Messages by member',
  get_appointment_distribution: 'Appointments',
  get_appointment_status_mix: 'Appointment status',
  get_booking_trend: 'Booking trend',
  get_business_hours: 'Business hours',
  get_appointment_demand_signal: 'Scheduling requests',
  compare_staffing_to_demand: 'Staffing vs demand',
};

export const toolLabel = (name) => TOOL_LABELS[name] || name;
